<div class="min-h-screen bg-gray-50">
  <!-- Header -->
  <app-header-nav></app-header-nav>

  <mat-toolbar class="border-b bg-white shadow-sm">
    <button mat-icon-button (click)="onBack()">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <h1 class="text-xl font-semibold text-gray-800 ml-4">Staff Management</h1>
    <span class="flex-auto"></span>
    <button mat-raised-button color="primary" (click)="openAddStaffDialog()">
      <mat-icon>person_add</mat-icon> Add Staff Member
    </button>
  </mat-toolbar>

  <!-- Loading Spinner -->
  <div class="flex items-center justify-center h-screen" *ngIf="isLoading">
    <mat-spinner></mat-spinner>
  </div>

  <div *ngIf="!isLoading" class="p-6 space-y-6">
    <!-- Overview Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Total Staff</mat-card-title>
          <mat-icon class="text-gray-500">people</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold">{{ getTotalStaff() }}</div>
          <p class="text-xs text-gray-600">{{ getActiveStaff() }} currently active</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Hours This Week</mat-card-title>
          <mat-icon class="text-blue-500">schedule</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold text-blue-500">{{ getTotalHours() }}h</div>
          <p class="text-xs text-gray-600">Total hours worked</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Total Tips</mat-card-title>
          <mat-icon class="text-green-500">attach_money</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold text-green-500">${{ getTotalTips() }}</div>
          <p class="text-xs text-gray-600">This week</p>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-header class="flex flex-row items-center justify-between space-y-0 pb-2">
          <mat-card-title class="text-sm">Avg Rating</mat-card-title>
          <mat-icon class="text-yellow-500">star</mat-icon>
        </mat-card-header>
        <mat-card-content>
          <div class="text-2xl font-bold text-yellow-500">{{ getAverageRating() }}</div>
          <p class="text-xs text-gray-600">Staff performance</p>
        </mat-card-content>
      </mat-card>
    </div>

    <!-- Tabs -->
    <mat-tab-group [(selectedIndex)]="selectedTabIndex" (selectedTabChange)="onTabChange($event)">
      <mat-tab [label]="'All Staff (' + getTotalStaff() + ')'">
        <div class="mt-4">
          <mat-card>
            <div class="mat-elevation-z2 rounded-lg overflow-hidden">
              <table mat-table [dataSource]="dataSource" matSort class="w-full">
                <!-- Name Column -->
                <ng-container matColumnDef="name">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header> Name </th>
                  <td mat-cell *matCellDef="let staff">
                    <div>
                      <div class="font-medium">{{ staff.name }}</div>
                      <div class="text-sm text-gray-600">{{ staff.email }}</div>
                    </div>
                  </td>
                </ng-container>

                <!-- Role Column -->
                <ng-container matColumnDef="role">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header> Role </th>
                  <td mat-cell *matCellDef="let staff">
                    <mat-chip [color]="getRoleColor(staff.role)">{{ staff.role }}</mat-chip>
                  </td>
                </ng-container>

                <!-- Status Column -->
                <ng-container matColumnDef="status">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header> Status </th>
                  <td mat-cell *matCellDef="let staff">
                    <mat-chip [color]="getStatusColor(staff.isActive)">{{ staff.isActive ? 'Active' : 'Inactive'
                      }}</mat-chip>
                  </td>
                </ng-container>

                <!-- Hours Column -->
                <ng-container matColumnDef="hours">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header> Hours </th>
                  <td mat-cell *matCellDef="let staff">
                    <div *ngIf="editingStaffId !== staff.id">{{ staff.hoursWorked || 0 }}h</div>
                    <div *ngIf="editingStaffId === staff.id">
                      <input type="number" [(ngModel)]="editingStaff!.hoursWorked" min="0"
                        class="w-16 border rounded p-1 text-sm bg-gray-50">
                    </div>
                  </td>
                </ng-container>

                <!-- Rate Column -->
                <ng-container matColumnDef="rate">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header> Rate </th>
                  <td mat-cell *matCellDef="let staff">
                    <div *ngIf="editingStaffId !== staff.id">${{ staff.hourlyRate || 0 }}/hr</div>
                    <div *ngIf="editingStaffId === staff.id">
                      <input type="number" [(ngModel)]="editingStaff!.hourlyRate" min="0"
                        class="w-16 border rounded p-1 text-sm bg-gray-50">
                    </div>
                  </td>
                </ng-container>

                <!-- Tips Column -->
                <ng-container matColumnDef="tips">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header> Tips </th>
                  <td mat-cell *matCellDef="let staff">
                    <div *ngIf="editingStaffId !== staff.id">${{ staff.totalTips || 0 }}</div>
                    <div *ngIf="editingStaffId === staff.id">
                      <input type="number" [(ngModel)]="editingStaff!.totalTips" min="0"
                        class="w-16 border rounded p-1 text-sm bg-gray-50">
                    </div>
                  </td>
                </ng-container>

                <!-- Rating Column -->
                <ng-container matColumnDef="rating">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header> Rating </th>
                  <td mat-cell *matCellDef="let staff">
                    <div *ngIf="editingStaffId !== staff.id" class="flex items-center gap-1">
                      <mat-icon class="text-yellow-500 text-sm">star</mat-icon>
                      {{ staff.rating || 0 }}
                    </div>
                    <div *ngIf="editingStaffId === staff.id">
                      <input type="number" [(ngModel)]="editingStaff!.rating" max="5" min="0"
                        class="w-16 border rounded p-1 text-sm bg-gray-50">
                    </div>
                  </td>
                </ng-container>

                <!-- Last Active Column -->
                <ng-container matColumnDef="lastActive">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header> Last Active </th>
                  <td mat-cell *matCellDef="let staff">
                    <div *ngIf="editingStaffId !== staff.id"> {{ staff.lastActive | date:'short' }} </div>
                    <div *ngIf="editingStaffId === staff.id">
                      <input type="date" [(ngModel)]="editingStaff!.lastActive"
                        class="w-16 border rounded p-1 text-sm bg-gray-50">
                    </div>
                  </td>
                </ng-container>

                <!-- Actions Column -->
                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef> Actions </th>
                  <td mat-cell *matCellDef="let staff">
                    <div class="flex gap-1" *ngIf="editingStaffId !== staff.id">
                      <!-- Restored: this was commented out, so the table every
                           manager sees first offered only Delete, and editing was
                           reachable only by switching to the Active tab. -->
                      <button mat-icon-button (click)="editStaff(staff); $event.stopPropagation()"
                              aria-label="Edit details" matTooltip="Edit details">
                        <mat-icon>edit</mat-icon>
                      </button>
                      <button mat-icon-button [color]="onShift(staff) ? 'warn' : 'primary'"
                              (click)="toggleShift(staff); $event.stopPropagation()"
                              [attr.aria-label]="onShift(staff) ? 'Clock out' : 'Clock in'"
                              [matTooltip]="onShift(staff) ? 'Clock out' : 'Clock in'">
                        <mat-icon>{{ onShift(staff) ? 'logout' : 'login' }}</mat-icon>
                      </button>
                      <button mat-icon-button (click)="recordTips(staff); $event.stopPropagation()"
                              aria-label="Record tips" matTooltip="Record tips">
                        <mat-icon>payments</mat-icon>
                      </button>
                      <mat-form-field class="w-32 text-sm">
                        <mat-select [value]="getStaffStatus(staff)"
                          (selectionChange)="updateStaffStatus(staff, $event.value)">
                          <mat-option value="active">Active</mat-option>
                          <mat-option value="on-break">On Break</mat-option>
                          <mat-option value="stop">Stop</mat-option>
                        </mat-select>
                      </mat-form-field>
                      <button mat-icon-button color="warn" (click)="deleteStaff(staff.id)">
                        <mat-icon>delete</mat-icon>
                      </button>
                    </div>
                    <div class="flex gap-1" *ngIf="editingStaffId === staff.id">
                      <button mat-icon-button color="primary" (click)="saveStaffEdit()" matTooltip="Save Changes">
                        <mat-icon>check_circle</mat-icon>
                      </button>
                      <button mat-icon-button color="warn" (click)="cancelEdit()" matTooltip="Cancel">
                        <mat-icon>cancel</mat-icon>
                      </button>
                    </div>
                  </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedColumns;" class="element-row"></tr>
              </table>

              <!-- Bound to the server's total, so Next actually fetches the
                   next page. It used to render with nothing behind it. -->
              <mat-paginator [length]="totalStaff"
                             [pageIndex]="pageIndex"
                             [pageSize]="pageSize"
                             [pageSizeOptions]="[5, 10, 25, 100]"
                             (page)="onPage($event)"
                             aria-label="Select page of staff"></mat-paginator>
            </div>
          </mat-card>
        </div>
      </mat-tab>

      <mat-tab [label]="'Active (' + getActiveStaff() + ')'">
        <div class="mt-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <mat-card *ngFor="let staff of getActiveStaffMembers()" class="border-green-200">
              <mat-card-header>
                <mat-card-title class="flex items-center justify-between">
                  <span>{{ staff.name }}</span>
                  <mat-chip [color]="getRoleColor(staff.role)">{{ staff.role }}</mat-chip>
                </mat-card-title>
              </mat-card-header>
              <mat-card-content class="space-y-2">
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Hours:</span>
                  <span>{{ staff.hoursWorked || 0 }}h</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Rate:</span>
                  <span>${{ staff.hourlyRate || 0 }}/hr</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Tips:</span>
                  <span class="text-green-500">${{ staff.totalTips || 0 }}</span>
                </div>
                <div class="flex justify-between" *ngIf="onShift(staff)">
                  <span class="text-sm text-gray-600">On shift since:</span>
                  <span class="text-sm font-medium text-green-700">
                    {{ staff.shiftStartedAt | date:'shortTime' }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-sm text-gray-600">Rating:</span>
                  <div class="flex items-center gap-1">
                    <mat-icon class="text-yellow-500 text-sm">star</mat-icon>
                    {{ staff.rating || 0 }}
                  </div>
                </div>
                <div class="flex gap-2 mt-3">
                  <button mat-raised-button color="primary" (click)="editStaff(staff)">
                    <mat-icon>edit</mat-icon>
                    Edit
                  </button>
                  <button mat-raised-button [color]="onShift(staff) ? 'warn' : 'accent'"
                          [attr.aria-label]="onShift(staff) ? 'Clock out' : 'Clock in'"
                          (click)="toggleShift(staff)">
                    <mat-icon>{{ onShift(staff) ? 'logout' : 'login' }}</mat-icon>
                    {{ onShift(staff) ? 'Clock out' : 'Clock in' }}
                  </button>
                  <button mat-stroked-button aria-label="Record tips" (click)="recordTips(staff)">
                    <mat-icon>payments</mat-icon>
                    Tips
                  </button>
                  <button mat-stroked-button (click)="viewStaffDetails(staff)">
                    <mat-icon>visibility</mat-icon>
                    View Details
                  </button>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
        </div>
      </mat-tab>

      <mat-tab label="Performance">
        <div class="mt-4">
          <mat-card>
            <mat-card-header>
              <mat-card-title>Staff Performance Overview</mat-card-title>
              <mat-card-subtitle>Performance metrics and ratings</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
              <div class="space-y-4">
                <div *ngFor="let staff of getStaffMembers()" class="p-4 border rounded-lg">
                  <div class="flex items-center justify-between mb-2">
                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <mat-icon class="text-white">person</mat-icon>
                      </div>
                      <div>
                        <h3 class="font-semibold">{{ staff.name }}</h3>
                        <p class="text-sm text-gray-600">{{ staff.role }}</p>
                      </div>
                    </div>
                    <div class="flex items-center gap-1">
                      <mat-icon class="text-yellow-500">star</mat-icon>
                      <span class="font-semibold">{{ staff.rating || 0 }}</span>
                    </div>
                  </div>
                  <div class="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span class="text-gray-600">Hours:</span>
                      <span class="font-semibold">{{ staff.hoursWorked || 0 }}h</span>
                    </div>
                    <div>
                      <span class="text-gray-600">Tips:</span>
                      <span class="font-semibold text-green-500">${{ staff.totalTips || 0 }}</span>
                    </div>
                    <div>
                      <span class="text-gray-600">Status:</span>
                      <mat-chip [color]="getStatusColor(staff.isActive)" class="text-xs">
                        {{ staff.isActive ? 'Active' : 'Inactive' }}
                      </mat-chip>
                    </div>
                  </div>
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-tab>
    </mat-tab-group>
  </div>

  <!-- Edit Staff Dialog -->
  <div *ngIf="showEditStaffDialog && editingStaff"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold">Edit Staff Member</h2>
        <button mat-icon-button (click)="closeEditStaffDialog()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <mat-form-field>
          <mat-label>Full Name</mat-label>
          <input matInput [(ngModel)]="editingStaff.name" name="editName" required placeholder="Enter full name">
        </mat-form-field>

        <mat-form-field>
          <mat-label>Email</mat-label>
          <input matInput type="email" [(ngModel)]="editingStaff.email" name="editEmail" required
            placeholder="Enter email address">
        </mat-form-field>

        <mat-form-field>
          <mat-label>Phone Number</mat-label>
          <input matInput [(ngModel)]="editingStaff.phone" name="editPhone" placeholder="Enter phone number">
        </mat-form-field>

        <mat-form-field>
          <mat-label>Role</mat-label>
          <mat-select [(ngModel)]="editingStaff.role" name="editRole" required>
            <mat-option value="MANAGER">Manager</mat-option>
            <mat-option value="BARTENDER">Bartender</mat-option>
            <mat-option value="WAITER">Waiter</mat-option>
            <mat-option value="HOST">Host</mat-option>
            <mat-option value="CHEF">Chef</mat-option>
            <mat-option value="CASHIER">Cashier</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field>
          <mat-label>Hourly Rate ($)</mat-label>
          <input matInput type="number" [(ngModel)]="editingStaff.hourlyRate" name="editHourlyRate"
            placeholder="Enter hourly rate">
        </mat-form-field>

        <mat-form-field>
          <mat-label>Hours Worked</mat-label>
          <input matInput type="number" [(ngModel)]="editingStaff.hoursWorked" name="editHoursWorked"
            placeholder="Enter hours worked">
        </mat-form-field>

        <!-- The roster has always had a Rating column, but nothing in the UI
             could set it: the only rating input lives in the inline row editor,
             which is shown when editingStaffId === staff.id, and the method that
             sets editingStaffId (toggleEditMode) is not called from anywhere.
             The Edit button opens THIS dialog, so the field belongs here. -->
        <mat-form-field>
          <mat-label>Rating (0-5)</mat-label>
          <input matInput type="number" min="0" max="5" step="0.1"
            [(ngModel)]="editingStaff.rating" name="editRating"
            aria-label="Staff rating out of five" placeholder="Rate this person 0 to 5">
        </mat-form-field>
      </div>

      <div class="flex gap-2 justify-end mt-6">
        <button mat-stroked-button (click)="closeEditStaffDialog()">
          Cancel
        </button>
        <button mat-raised-button color="primary" (click)="updateStaffFromDialog()">
          <mat-icon>save</mat-icon>
          Save Changes
        </button>
      </div>
    </div>
  </div>

  <!-- Staff Details Dialog -->
  <div *ngIf="showDetailsDialog && selectedStaff"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
      <div class="flex justify-between items-center mb-6 border-b pb-4">
        <div class="flex items-center gap-4">
          <div
            class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-xl">
            {{ selectedStaff.name.charAt(0) }}
          </div>
          <div>
            <h2 class="text-xl font-semibold">{{ selectedStaff.name }}</h2>
            <p class="text-gray-500">{{ selectedStaff.role }}</p>
          </div>
        </div>
        <button mat-icon-button (click)="closeDetailsDialog()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <p class="text-sm text-gray-500">Email</p>
            <p class="font-medium">{{ selectedStaff.email }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Phone</p>
            <p class="font-medium">{{ selectedStaff.phone || 'N/A' }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-500">Status</p>
            <mat-chip [color]="getStatusColor(selectedStaff.isActive)" class="mt-1">
              {{ selectedStaff.isActive ? 'Active' : 'Inactive' }}
            </mat-chip>
          </div>
          <div>
            <p class="text-sm text-gray-500">Joined</p>
            <p class="font-medium">{{ selectedStaff.createdAt | date }}</p>
          </div>
        </div>

        <div class="bg-gray-50 p-4 rounded-lg mt-4">
          <h3 class="font-medium mb-3">Performance Metrics</h3>
          <div class="grid grid-cols-3 gap-2 text-center">
            <div class="bg-white p-2 rounded shadow-sm">
              <div class="text-xs text-gray-500">Rating</div>
              <div class="font-bold flex items-center justify-center gap-1 text-yellow-600">
                {{ selectedStaff.rating || 0 }} <mat-icon class="text-xs">star</mat-icon>
              </div>
            </div>
            <div class="bg-white p-2 rounded shadow-sm">
              <div class="text-xs text-gray-500">Hours</div>
              <div class="font-bold text-blue-600">{{ selectedStaff.hoursWorked || 0 }}h</div>
            </div>
            <div class="bg-white p-2 rounded shadow-sm">
              <div class="text-xs text-gray-500">Tips</div>
              <div class="font-bold text-green-600">${{ selectedStaff.totalTips || 0 }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 flex justify-end">
        <button mat-button (click)="closeDetailsDialog()">Close</button>
      </div>
    </div>
  </div>


  <!-- Add Staff Dialog -->
  <div *ngIf="showAddStaffDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold">Add New Staff Member</h2>
        <button mat-icon-button (click)="closeAddStaffDialog()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <form (ngSubmit)="addNewStaff()" class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <mat-form-field>
            <mat-label>Full Name</mat-label>
            <input matInput [(ngModel)]="newStaff.name" name="name" required placeholder="Enter full name">
          </mat-form-field>

          <mat-form-field>
            <mat-label>Email</mat-label>
            <input matInput type="email" [(ngModel)]="newStaff.email" name="email" required
              placeholder="Enter email address">
          </mat-form-field>

          <mat-form-field>
            <mat-label>Phone Number</mat-label>
            <input matInput [(ngModel)]="newStaff.phone" name="phone" placeholder="Enter phone number">
          </mat-form-field>

          <mat-form-field>
            <mat-label>Role</mat-label>
            <mat-select [(ngModel)]="newStaff.role" name="role" required>
              <mat-option value="MANAGER">Manager</mat-option>
              <mat-option value="BARTENDER">Bartender</mat-option>
              <mat-option value="WAITER">Waiter</mat-option>
              <mat-option value="HOST">Host</mat-option>
              <mat-option value="CHEF">Chef</mat-option>
              <mat-option value="CASHIER">Cashier</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field>
            <mat-label>Hourly Rate ($)</mat-label>
            <input matInput type="number" [(ngModel)]="newStaff.hourlyRate" name="hourlyRate"
              placeholder="Enter hourly rate">
          </mat-form-field>

          <mat-form-field>
            <mat-label>Initial Hours Worked</mat-label>
            <input matInput type="number" [(ngModel)]="newStaff.hoursWorked" name="hoursWorked"
              placeholder="Enter hours worked">
          </mat-form-field>
        </div>

        <div class="flex gap-2 justify-end mt-6">
          <button mat-stroked-button type="button" (click)="closeAddStaffDialog()">
            Cancel
          </button>
          <button mat-raised-button color="primary" type="submit">
            <mat-icon>person_add</mat-icon>
            Add Staff Member
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Delete Confirmation Dialog -->
  <ng-template #deleteConfirmDialog>
    <h2 mat-dialog-title class="font-bold text-red-600">Delete Staff Member</h2>
    <mat-dialog-content>
      <p class="mb-4">Are you sure you want to delete this staff member? This action cannot be undone.</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end" class="gap-2">
      <button mat-stroked-button mat-dialog-close>Cancel</button>
      <button mat-raised-button color="warn" (click)="confirmDelete()">Delete</button>
    </mat-dialog-actions>
  </ng-template>

  <!-- Footer -->
  <app-footer></app-footer>
</div>